import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api, ApiError, type ChangeSetView, type LelandItem, type PlanWhen, type ScheduledChangeView } from '../api/client';
import { startImport, type ImportState } from '../components/ImportCard';

/** One line of the conversation as the panel shows it. */
export type LelandEntry =
  | { id: number; from: 'me'; text: string; file?: string }
  | { id: number; from: 'leland'; item: Exclude<LelandItem, { type: 'plan' } | { type: 'import' }> }
  | { id: number; from: 'leland'; item: { type: 'plan'; changeSet: ChangeSetView; when?: PlanWhen }; state: PlanState }
  /** People from a spreadsheet: the card keeps its own progress here, so it survives the panel closing. */
  | { id: number; from: 'leland'; item: { type: 'import' }; imp: ImportState };

export type PlanState =
  | { phase: 'planned' }
  | { phase: 'applying' }
  /** Applied; `putBack`: put back by itself later (for a while, 1.12.15). */
  | { phase: 'applied'; result: ChangeSetView; putBack?: ScheduledChangeView }
  /** Approved for later (1.12.15): nothing has changed yet. */
  | { phase: 'scheduled'; scheduled: ScheduledChangeView }
  | { phase: 'failed'; result?: ChangeSetView; message: string } | { phase: 'undoing'; result: ChangeSetView } | { phase: 'undone'; result: ChangeSetView } | { phase: 'discarded' };

type DistributiveOmit<T, K extends keyof T> = T extends unknown ? Omit<T, K> : never;

interface LelandState {
  enabled: boolean | undefined;
  open: boolean;
  setOpen: (open: boolean) => void;
  entries: LelandEntry[];
  busy: boolean;
  /** What to put in the box when the panel opens (from a nudge). */
  draft: string;
  setDraft: (text: string) => void;
  send: (text: string, uploadId?: string) => Promise<void>;
  /** Upload a spreadsheet and hand it to Leland (opens the panel if it's closed). */
  attach: (file: File, text?: string) => Promise<void>;
  updateImport: (entryId: number, fn: (s: ImportState) => ImportState) => void;
  domain: string;
  apply: (entryId: number) => Promise<void>;
  undo: (entryId: number) => Promise<void>;
  discard: (entryId: number) => Promise<void>;
  /** A plan approved for later, or put back later (1.12.15): cancel that, or do it now. */
  later: (entryId: number, what: 'cancel' | 'run') => Promise<void>;
  reset: () => void;
}

const Ctx = createContext<LelandState | null>(null);

export function useLeland(): LelandState | null {
  return useContext(Ctx);
}

/** One conversation per company page visit, shared by the floating panel and the Leland page. */
export function LelandProvider({ domain, children }: { domain: string; children: ReactNode }) {
  const [enabled, setEnabled] = useState<boolean>();
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState<LelandEntry[]>([]);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState('');
  const conversation = useRef<string | undefined>(undefined);
  const nextId = useRef(1);

  useEffect(() => {
    api
      .leland()
      .then((r) => setEnabled(r.enabled))
      .catch(() => setEnabled(false));
  }, []);

  const add = useCallback((e: DistributiveOmit<LelandEntry, 'id'>) => setEntries((list) => [...list, { ...e, id: nextId.current++ } as LelandEntry]), []);
  const patchPlan = useCallback((entryId: number, state: PlanState) => setEntries((list) => list.map((e) => (e.id === entryId && 'state' in e ? { ...e, state } : e))), []);

  const updateImport = useCallback(
    (entryId: number, fn: (s: ImportState) => ImportState) => setEntries((list) => list.map((e) => (e.id === entryId && 'imp' in e ? { ...e, imp: fn(e.imp) } : e))),
    []
  );

  /** One message to Leland; `shown` is what the conversation shows for it (a file's name, say). */
  const talk = useCallback(
    async (t: string, uploadId?: string) => {
      setBusy(true);
      try {
        let r;
        try {
          r = await api.lelandSend(domain, t, conversation.current, uploadId);
        } catch (e) {
          // The conversation ended on the server (a restart, or it was long): carry on in a new one.
          if (e instanceof ApiError && e.code === 'CONVERSATION_GONE') {
            conversation.current = undefined;
            r = await api.lelandSend(domain, t, undefined, uploadId);
          } else throw e;
        }
        conversation.current = r.conversationId;
        for (const item of r.items) {
          if (item.type === 'plan') add({ from: 'leland', item, state: { phase: 'planned' } });
          else if (item.type === 'import') {
            // A newer plan for the same file replaces the card from before.
            setEntries((list) => list.map((e) => ('imp' in e && e.imp.view.id === item.import.id && e.imp.status.phase === 'review' ? { ...e, imp: { ...e.imp, status: { phase: 'replaced' } } } : e)));
            add({ from: 'leland', item: { type: 'import' }, imp: startImport(item.import) });
          } else add({ from: 'leland', item });
        }
      } catch (e) {
        add({ from: 'leland', item: { type: 'error', text: e instanceof Error ? e.message : 'That didn’t work. Try again.' } });
      } finally {
        setBusy(false);
      }
    },
    [add, domain]
  );

  const send = useCallback(
    async (text: string, uploadId?: string) => {
      const t = text.trim();
      if (!t || busy) return;
      add({ from: 'me', text: t });
      await talk(t, uploadId);
    },
    [add, busy, talk]
  );

  const attach = useCallback(
    async (file: File, text?: string) => {
      if (busy) return;
      setOpen(true);
      const t = text?.trim() || 'Here’s a spreadsheet of our people. Please set them up.';
      add({ from: 'me', text: t, file: file.name });
      setBusy(true);
      let uploadId: string;
      try {
        uploadId = (await api.uploadSheet(domain, file)).id;
      } catch (e) {
        add({ from: 'leland', item: { type: 'error', text: e instanceof Error ? e.message : 'That file couldn’t be read.' } });
        setBusy(false);
        return;
      }
      await talk(t, uploadId);
    },
    [add, busy, domain, talk]
  );

  const planOf = useCallback((entryId: number) => entries.find((e): e is Extract<LelandEntry, { state: PlanState }> => e.id === entryId && 'state' in e), [entries]);

  const apply = useCallback(
    async (entryId: number) => {
      const e = planOf(entryId);
      if (!e) return;
      patchPlan(entryId, { phase: 'applying' });
      try {
        const when = e.item.when;
        if (when?.kind === 'at') {
          const r = await api.scheduleForLeland(e.item.changeSet.id, when.at, when.summary);
          patchPlan(entryId, { phase: 'scheduled', scheduled: r.scheduled });
          return;
        }
        const r = await api.applyForLeland(e.item.changeSet.id, when?.kind === 'until' ? { at: when.at, summary: when.summary } : undefined);
        if (r.changeSet.status === 'applied') patchPlan(entryId, { phase: 'applied', result: r.changeSet, ...(r.scheduled ? { putBack: r.scheduled } : {}) });
        else patchPlan(entryId, { phase: 'failed', result: r.changeSet, message: r.changeSet.error ?? 'Not everything could be applied, so it was put back.' });
      } catch (err) {
        patchPlan(entryId, { phase: 'failed', message: err instanceof Error ? err.message : 'That didn’t work.' });
      }
    },
    [patchPlan, planOf]
  );

  const later = useCallback(
    async (entryId: number, what: 'cancel' | 'run') => {
      const e = planOf(entryId);
      if (!e) return;
      const st = e.state;
      const pending = st.phase === 'scheduled' ? st.scheduled : st.phase === 'applied' ? st.putBack : undefined;
      if (!pending) return;
      try {
        if (what === 'cancel') {
          await api.cancelScheduled(domain, pending.id);
          patchPlan(entryId, st.phase === 'scheduled' ? { phase: 'discarded' } : { phase: 'applied', result: (st as { result: ChangeSetView }).result });
          return;
        }
        const r = await api.runScheduled(domain, pending.id);
        const cs = (await api.changeSet(pending.changeSetId)).changeSet;
        if (r.scheduled.status === 'failed') patchPlan(entryId, { phase: 'failed', result: cs, message: r.scheduled.outcome });
        else if (st.phase === 'scheduled') patchPlan(entryId, cs.status === 'applied' ? { phase: 'applied', result: cs } : { phase: 'failed', result: cs, message: r.scheduled.outcome || 'It didn’t run.' });
        else patchPlan(entryId, { phase: 'undone', result: cs });
      } catch (err) {
        patchPlan(entryId, { phase: 'failed', ...('result' in st ? { result: st.result } : {}), message: err instanceof Error ? err.message : 'That didn’t work.' });
      }
    },
    [domain, patchPlan, planOf]
  );

  const undo = useCallback(
    async (entryId: number) => {
      const e = planOf(entryId);
      if (!e || e.state.phase !== 'applied') return;
      const result = e.state.result;
      patchPlan(entryId, { phase: 'undoing', result });
      try {
        const r = await api.undoForLeland(result.id);
        patchPlan(entryId, r.undo.status === 'applied' ? { phase: 'undone', result } : { phase: 'failed', result, message: r.undo.error ?? 'It couldn’t all be undone.' });
      } catch (err) {
        patchPlan(entryId, { phase: 'failed', result, message: err instanceof Error ? err.message : 'That didn’t work.' });
      }
    },
    [patchPlan, planOf]
  );

  const discard = useCallback(
    async (entryId: number) => {
      const e = planOf(entryId);
      if (!e) return;
      await api.discard(e.item.changeSet.id).catch(() => undefined);
      patchPlan(entryId, { phase: 'discarded' });
    },
    [patchPlan, planOf]
  );

  const reset = useCallback(() => {
    if (conversation.current) void api.lelandForget(domain, conversation.current).catch(() => undefined);
    conversation.current = undefined;
    setEntries([]);
  }, [domain]);

  const value = useMemo<LelandState>(
    () => ({ enabled, open, setOpen, entries, busy, draft, setDraft, send, attach, updateImport, domain, apply, undo, discard, later, reset }),
    [enabled, open, entries, busy, draft, send, attach, updateImport, domain, apply, undo, discard, later, reset]
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Something to ask Leland, from the title of the dialog someone just opened. */
export function suggestionFor(title: string): string {
  const t = title.replace(/[“”"]/g, '');
  const m = /^(Add|Edit|Change|Remove|Quick add|Move)\s+(.*)$/i.exec(t);
  if (!m) return '';
  const what = (m[2] ?? '').trim();
  switch ((m[1] ?? '').toLowerCase()) {
    case 'add':
    case 'quick add':
      return what ? `Add a ${what.replace(/^(a|an)\s+/i, '').toLowerCase()} ` : '';
    case 'edit':
    case 'change':
      return what ? `Change ${what}: ` : '';
    case 'remove':
      return what ? `Remove ${what.replace(/\?$/, '')}` : '';
    default:
      return '';
  }
}
